import React from 'react';
import type {
  WebsiteTemplateRegistryTemplateSummary,
  WebsiteUpsertRequest,
} from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import { buildWebsiteTemplatePreviewModel } from '../../utils/template-preview.util';

/** Props for the descriptor-based design preview. */
interface WebsiteTemplateFallbackPreviewProps {
  /** Design whose preview descriptor supplies sample content. */
  template?: WebsiteTemplateRegistryTemplateSummary;
  /** Draft whose brand and copy override the sample content. */
  form: WebsiteUpsertRequest;
  /** Notice shown under the sample, when the caller needs one. */
  notice?: string;
}

/**
 * Renders an approximate design preview from the registry descriptor, for
 * designs whose package has no mock preview.
 *
 * @param props - Design, draft, and optional notice.
 * @returns A hero block with the agency brand and two sample listings.
 */
export const WebsiteTemplateFallbackPreview: React.FC<
  WebsiteTemplateFallbackPreviewProps
> = ({ template, form, notice }) => {
  const model = buildWebsiteTemplatePreviewModel(template, form);
  const fallbackStyle: React.CSSProperties & Record<string, string> = {
    '--website-preview-primary': model.theme.primaryColor,
    '--website-preview-secondary': model.theme.secondaryColor,
    '--website-preview-accent': model.theme.accentColor,
  };

  return (
    <div className="p-5" style={fallbackStyle}>
      <div
        className="rounded-2xl bg-[var(--website-preview-primary)] bg-cover bg-center p-5 text-white"
        style={{ backgroundImage: `url(${model.heroImageUrl})` }}
      >
        <div className="rounded-2xl bg-black/45 p-5 backdrop-blur-sm">
          <Text weight="semibold" className="text-white">
            {model.siteName}
          </Text>
          <Text as="h4" size="h2" weight="semibold" className="mt-6 text-white">
            {model.headline}
          </Text>
          <Text size="tiny" className="mt-2 text-white/80">
            {model.description}
          </Text>
        </div>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {model.properties.slice(0, 2).map((property) => (
          <article
            key={`${property.title}-${property.price}`}
            className="rounded-xl border border-neutral-200 bg-white p-3"
          >
            <Text size="tiny" weight="semibold">
              {property.price}
            </Text>
            <Text weight="medium" className="mt-1 text-sm">
              {property.title}
            </Text>
            <Text size="tiny" color="secondary" className="mt-1">
              {property.propertyType} en {property.location}
            </Text>
          </article>
        ))}
      </div>
      {notice ? (
        <Text size="tiny" color="secondary" className="mt-4">
          {notice}
        </Text>
      ) : null}
    </div>
  );
};
